import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFieldError } from '@/components/ui/form-field-error';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useChurchStore, type PublicChurch } from '@/stores/church-store';

export const SecuritySettingsPage = () => {
  const { t } = useTranslation('security');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const refreshCurrent = useChurchStore((state) => state.refreshCurrent);
  const restore = useChurchStore((state) => state.restore);
  const reset = useChurchStore((state) => state.reset);

  const isOwner = membership?.systemRole === 'owner';
  const [requireTotp, setRequireTotp] = useState(church?.requireTotpForPrivilegedRoles ?? false);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.patch<PublicChurch>('/churches/current', {
        requireTotpForPrivilegedRoles: requireTotp,
      }),
    onSuccess: async () => {
      toast.success(t('settings.saved'));
      await refreshCurrent();
      await restore();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(tErrors(err.code, err.params));
        return;
      }
      setError(tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete('/churches/current'),
    onSuccess: async () => {
      toast.success(t('settings.deleted'));
      setDeleteOpen(false);
      reset();
      await restore();
      void navigate('/churches/select');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  if (!church) return null;

  return (
    <div>
      <PageHeader
        title={t('settings.title')}
        description={t('settings.description')}
        tipId="security.passwordless"
      />

      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <SwitchField
          id="require-totp"
          label={t('settings.requireTotp')}
          tipId="security.requireTotp"
          checked={requireTotp}
          onCheckedChange={setRequireTotp}
        />

        <FormFieldError message={error ?? undefined} />

        <div className="flex justify-end gap-2">
          <Button type="submit" loading={save.isPending}>
            {t('settings.save')}
          </Button>
        </div>
      </form>

      {isOwner ? (
        <Card className="mt-10 max-w-xl border-danger/40">
          <CardHeader>
            <CardTitle>{t('settings.dangerZone')}</CardTitle>
            <CardDescription>{t('settings.dangerZoneDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-end">
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (church.canDelete === false) {
                  toast.error(tErrors('CHURCH_HAS_LINKED_RECORDS'));
                  return;
                }
                setDeleteOpen(true);
              }}
            >
              {t('settings.delete')}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('settings.confirmDelete.title')}
        description={t('settings.confirmDelete.description')}
        confirmLabel={t('settings.confirmDelete.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        typeToConfirm={{
          value: church.name,
          label: tCommon('confirm.typeToConfirm', { value: church.name }),
        }}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
};
